import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  TRIAL_STATUS_QUERY_KEY,
  useAiTrialQuotaQuery,
} from '@/shared/features/ai-quiz-generation/hooks/useAiTrialQuotaQuery';

import { type AiGenerateDockUsage } from '../utils/aiGenerateDockOutcome';

export type AiGenerateDockUsageView = {
  /** 무료 체험 한도가 걸린 사용자인가(게스트·FREE). 유료는 false. */
  limited: boolean;
  /** 남은 횟수. 아직 모르면 null. */
  remaining: number | null;
  exhausted: boolean;
};

const UNLIMITED: AiGenerateDockUsageView = {
  limited: false,
  remaining: null,
  exhausted: false,
};

/**
 * 생성기 남은 횟수. 처음은 `/quiz-builder/trial-status` 로 읽고, 이후는 생성기 응답
 * (respond.usage · 429 usage)이 정본이다 — 둘 다 같은 `ai_trial_usage` 를 본다.
 *
 * 조회 실패는 막지 않는다. 실제 판정은 서버가 실행 시점에 하고, 거절이면 그때 안내한다.
 *
 * 응답으로 받은 값은 이 훅 안에만 남으므로, 갱신할 때 trial-status 캐시도 무효화한다 — 성공하면
 * 입력창이 닫혀(언마운트) 그 값이 사라지는데, 다시 열 때 staleTime(1분) 안의 옛 횟수를 보이지 않게.
 */
export const useAiGenerateDockUsage = () => {
  const queryClient = useQueryClient();
  const { data, isQuotaLimited } = useAiTrialQuotaQuery();
  const [latest, setLatest] = useState<AiGenerateDockUsage | null>(null);

  const update = useCallback(
    (next: AiGenerateDockUsage) => {
      setLatest(next);
      void queryClient.invalidateQueries({ queryKey: TRIAL_STATUS_QUERY_KEY });
    },
    [queryClient],
  );

  /**
   * 사용량 없이 온 소진 응답(`usage` 는 선택 필드다) — 남은 0 으로 둔다. 한도는 아는 값이 있으면 그것을 쓴다.
   * 갱신과 같이 trial-status 도 무효화해 다시 열 때 옛 횟수를 보이지 않게 한다.
   */
  const markExhausted = useCallback(() => {
    update({
      state: 'blocked',
      remaining: 0,
      limit: latest?.limit ?? data?.lifetimeLimit ?? 0,
    });
  }, [data?.lifetimeLimit, latest?.limit, update]);

  const view = useMemo((): AiGenerateDockUsageView => {
    // 유료로 바뀌면(페이월에서 BASIC·체험 시작) 앞서 받은 소진 응답보다 지금 플랜이 먼저다.
    if (!isQuotaLimited) return UNLIMITED;
    if (latest) {
      // 서버는 유료 사용자를 remaining·limit -1 로 표시한다.
      if (latest.limit < 0 || latest.remaining < 0) return UNLIMITED;
      return {
        limited: true,
        remaining: latest.remaining,
        exhausted: latest.state === 'blocked' || latest.remaining === 0,
      };
    }
    if (!data) return { limited: true, remaining: null, exhausted: false };
    if (data.remaining < 0) return UNLIMITED;
    return {
      limited: true,
      remaining: data.remaining,
      exhausted: data.remaining === 0,
    };
  }, [data, isQuotaLimited, latest]);

  return { ...view, update, markExhausted };
};
